import React, { useState, useEffect, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import { useDispatch } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import { alertActions } from "_store";
import {
  Typography,
  Button,
  Box,
  FormControl,
  InputLabel,
  FormHelperText,
} from "@mui/material";
import Popper from "@mui/material/Popper";
import Grid from "@material-ui/core/Grid";
import { ERR_MSG } from "constants/labels";
import { CREATE_DEFAULT_FIELD_MAPPING, createAgencySchema } from "../../utils";

const AgencyEdit = ({
  agencyData,
  agencies,
  anchorEl,
  isOpen,
  onClose,
  handleRefresh,
  updateApi,
  header = "Agency",
  title = "Edit Agency",
  fieldMapping: fieldMappingOverride,
  portalID
}) => {
  const dispatch = useDispatch();
  const fm = { ...CREATE_DEFAULT_FIELD_MAPPING, ...fieldMappingOverride };
  const [isDisable, setIsDisable] = useState(false);

  const canBeOpen = isOpen && Boolean(anchorEl);
  const id = canBeOpen ? "edit-agency-popper" : undefined;

  const editSchema = createAgencySchema(fm);

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isValid },
    watch,
    trigger,
  } = useForm({
    resolver: yupResolver(editSchema),
    mode: "onBlur",
    defaultValues: {
      [fm.name]: agencyData?.[fm.name] || "",
      [fm.description]: agencyData?.[fm.description] || "",
    },
  });

  const watchedFields = watch();
  const prevFormValues = useRef(watchedFields);

  useEffect(() => {
    if (agencyData) {
      reset({
        [fm.name]: agencyData[fm.name] || "",
        [fm.description]: agencyData[fm.description] || "",
      });
    }
  }, [agencyData, reset, fm.name, fm.description]);

  useEffect(() => {
    const hasChanged = Object.keys(watchedFields).some((key) => {
      return watchedFields[key] !== prevFormValues.current[key];
    });

    if (hasChanged && isDisable) {
      setIsDisable(false);
    }

    prevFormValues.current = watchedFields;
  }, [watchedFields, isDisable]);

  const onSubmit = async (data) => {
    setIsDisable(true);
    try {
      dispatch(alertActions.clear());

      // Check for duplicate names (excluding current item)
      if (agencies && agencies.length > 0) {
        const duplicateNames = agencies
          .filter(
            (oldItem) =>
              oldItem[fm.name].toLowerCase() === data[fm.name].toLowerCase() &&
              oldItem[fm.id] !== agencyData[fm.id],
          )
          .map((item) => item[fm.name]);

        if (duplicateNames.length > 0) {
          dispatch(
            alertActions.error({
              message: `${fm.nameLabel} already exists.`,
              header,
            }),
          );
          setIsDisable(false);
          return;
        }
      }

      const transformedData = {
        Data: {
          [fm.id]: agencyData[fm.id] || 0,
          [fm.name]: data[fm.name],
          [fm.description]: data[fm.description],
          RoleID: agencyData.RoleID || "",
          PortalID: portalID,
          IsActive:
            agencyData.IsActive !== undefined ? agencyData.IsActive : true,
        },
      };

      const result = await dispatch(updateApi(transformedData)).unwrap();

      if (!result || !result?.Succeeded) {
        dispatch(
          alertActions.error({
            message: result?.Message,
            header,
          }),
        );
        setIsDisable(false);
        return;
      }

      dispatch(
        alertActions.success({
          message: result?.Message || "Updated successfully",
          header,
        }),
      );

      setTimeout(() => {
        handleRefresh();
      }, 1000);
    } catch (error) {
      dispatch(
        alertActions.error({
          message: error.message ? ERR_MSG["073"] : error,
          header,
        }),
      );
      setIsDisable(false);
    } finally {
      onClose();
    }
  };

  const handleBlur = async (fieldName) => {
    await trigger(fieldName);
  };

  const handleCancelClick = () => {
    reset();
    onClose();
  };

  return (
    <Popper
      id={id}
      open={canBeOpen}
      anchorEl={anchorEl}
      className="Filtercontainer"
      placement="left-start"
    >
      <Box
        sx={{ border: 1, p: 1, bgcolor: "background.paper" }}
        className="Filtercontainerinner CreateMarketerhight Border"
      >
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="Registrationcontainer marketerFiltercontainer"
        >
          <Typography component="div" className="userprofilelist">
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h2" className="userprofilelistcontent">
                  {title}
                </Typography>
              </Grid>
            </Grid>
          </Typography>

          <FormControl
            fullWidth
            error={!!errors[fm.name]}
            className="CustomFormControl"
            style={{ marginBottom: "20px" }}
          >
            <Controller
              name={fm.name}
              control={control}
              render={({ field }) => (
                <>
                  <InputLabel shrink>{fm.nameLabel}</InputLabel>
                  <input
                    {...field}
                    type="text"
                    maxLength={100}
                    onBlur={() => handleBlur(fm.name)}
                    placeholder={`Enter ${fm.nameLabel}`}
                    style={{
                      width: "100%",
                      padding: "16.5px 14px",
                      fontSize: "1rem",
                      border: errors[fm.name]
                        ? "1px solid #d32f2f"
                        : "1px solid rgba(0, 0, 0, 0.23)",
                      borderRadius: "4px",
                      fontFamily: "inherit",
                      marginTop: "20px",
                    }}
                  />
                </>
              )}
            />
            {errors[fm.name] && (
              <FormHelperText>{errors[fm.name].message}</FormHelperText>
            )}
          </FormControl>

          <FormControl
            fullWidth
            error={!!errors[fm.description]}
            className="CustomFormControl"
            style={{ marginBottom: "20px" }}
          >
            <Controller
              name={fm.description}
              control={control}
              render={({ field }) => (
                <>
                  <InputLabel shrink>{fm.descriptionLabel}</InputLabel>
                  <textarea
                    {...field}
                    maxLength={255}
                    onBlur={() => handleBlur(fm.description)}
                    placeholder={`Enter ${fm.descriptionLabel}`}
                    rows={3}
                    style={{
                      width: "100%",
                      padding: "16.5px 14px",
                      fontSize: "1rem",
                      border: errors[fm.description]
                        ? "1px solid #d32f2f"
                        : "1px solid rgba(0, 0, 0, 0.23)",
                      borderRadius: "4px",
                      fontFamily: "inherit",
                      marginTop: "20px",
                      resize: "vertical",
                    }}
                  />
                </>
              )}
            />
            {errors[fm.description] && (
              <FormHelperText>
                {errors[fm.description].message}
              </FormHelperText>
            )}
          </FormControl>

          <Typography component="div" className="CreateMarketerbutton">
            <Button
              type="submit"
              variant="contained"
              className="submitbutton"
              color="primary"
              disabled={!isValid || isDisable}
            >
              Update
            </Button>
            <Button
              variant="contained"
              color="red"
              className="cancelbutton"
              onClick={handleCancelClick}
            >
              Cancel
            </Button>
          </Typography>
        </form>
      </Box>
    </Popper>
  );
};

export default AgencyEdit;